import React, { useEffect, useState } from 'react';
import SEO from '../../components/SEO';
import PageLoader from '../../components/PageLoader';
import api from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';
import Pagination from '../../components/Pagination';
import { useSidebar } from '../../contexts/SidebarContext';
import { showSuccess, showError, showConfirm } from '../../utils/toast';
import { Star } from 'lucide-react';

const AdminContentTagsContent = () => {
  const { isCollapsed } = useSidebar();
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', active: true, featured: false });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('a-z');
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchItems();
  }, [currentPage, search, sort, limit]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit, sort: sort === 'z-a' ? 'z-a' : 'a-z' };
      if (search) params.search = search;
      const res = await api.get('/content-tags', { params });
      setItems(res.data.contentTags || []);
      setPagination(res.data.pagination || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/content-tags/${editing._id}`, formData);
        showSuccess('Content tag updated.');
      } else {
        await api.post('/content-tags', formData);
        showSuccess('Content tag created.');
      }
      setShowModal(false);
      setEditing(null);
      setFormData({ name: '', active: true, featured: false });
      fetchItems();
    } catch (e) {
      showError(e.response?.data?.message || 'Error saving');
    }
  };

  const handleEdit = (item) => {
    setEditing(item);
    setFormData({ name: item.name || '', active: item.active !== false, featured: !!item.featured });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    showConfirm('Delete this content tag?', async () => {
      try {
        await api.delete(`/content-tags/${id}`);
        showSuccess('Deleted.');
        fetchItems();
      } catch (e) {
        showError(e.response?.data?.message || 'Error deleting');
      }
    });
  };

  return (
    <>
      <SEO title="Content Tags – Admin" noindex />
      <main className={`flex-1 transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'}`}>
        <div className="container-custom py-6 md:py-12">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-50">Content Tags</h1>
            <button
              type="button"
              onClick={() => { setEditing(null); setFormData({ name: '', active: true, featured: false }); setShowModal(true); }}
              className="btn-primary text-sm"
            >
              Add Tag
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mb-6">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(searchInput); setCurrentPage(1); } }}
              placeholder="Search..."
              className="flex-1 min-w-[180px] px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
            />
            <button type="button" onClick={() => { setSearch(searchInput); setCurrentPage(1); }} className="px-4 py-2 bg-primary-600 text-white rounded-lg">Search</button>
            <select value={sort} onChange={(e) => { setSort(e.target.value); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
              <option value="a-z">A–Z</option>
              <option value="z-a">Z–A</option>
            </select>
            <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
            </select>
          </div>

          {loading ? (
            <PageLoader message="Loading..." />
          ) : (
            <>
              <div className="card overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">#</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Slug</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Featured</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {items.map((item, i) => (
                      <tr key={item._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{(currentPage - 1) * limit + i + 1}</td>
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-50">{item.name}</td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-sm">{item.slug}</td>
                        <td className="px-4 py-3"><span className={`badge text-xs ${item.active ? 'badge-success' : 'badge-error'}`}>{item.active ? 'Active' : 'Inactive'}</span></td>
                        <td className="px-4 py-3">{item.featured ? <Star className="w-4 h-4 text-amber-500 fill-amber-500" title="Featured" /> : '–'}</td>
                        <td className="px-4 py-3">
                          <button type="button" onClick={() => handleEdit(item)} className="text-primary-600 dark:text-primary-400 hover:underline text-sm mr-3">Edit</button>
                          <button type="button" onClick={() => handleDelete(item._id)} className="text-error-600 dark:text-error-400 hover:underline text-sm">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(!items || items.length === 0) && (
                <div className="card p-12 text-center text-gray-600 dark:text-gray-400">No content tags.</div>
              )}
              {pagination && pagination.totalPages > 1 && (
                <div className="mt-6">
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalItems}
                    itemsPerPage={pagination.itemsPerPage}
                    itemsOnCurrentPage={pagination.itemsOnCurrentPage}
                    onPageChange={(p) => { setCurrentPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  />
                </div>
              )}
            </>
          )}

          {showModal && (
            <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-md">
                <h2 className="text-xl font-bold text-gray-800 dark:text-gray-50 mb-4">{editing ? 'Edit' : 'Add'} Content Tag</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Name *</label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="input-field" />
                  </div>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} className="rounded" />
                    <span className="text-gray-700 dark:text-gray-300">Active</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={formData.featured} onChange={(e) => setFormData({ ...formData, featured: e.target.checked })} className="rounded" />
                    <span className="text-gray-700 dark:text-gray-300">⭐ Featured</span>
                  </label>
                  <div className="flex gap-4 pt-4">
                    <button type="submit" className="btn-primary flex-1">{editing ? 'Update' : 'Create'}</button>
                    <button type="button" onClick={() => { setShowModal(false); setEditing(null); }} className="btn-secondary flex-1">Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

const AdminContentTags = () => (
  <AdminLayout>
    <AdminContentTagsContent />
  </AdminLayout>
);

export default AdminContentTags;
